import { formatNumberUsingUnit } from '../utils/formatUtils';
import React from 'react';
import PropTypes from 'prop-types';

const currentMonth = new Date().getMonth() + 1;
const yearPlusValue = currentMonth === 12 ? 1 : 0;
const percentValueSymbolArray = ['Close Rate', 'Cancellation Rate'];

// Helper function to safely access cell values with validation
const safeRenderValue = (cell, formatter, defaultValue = 'N/A') => {
  // Check if cell value exists and isn't null/undefined/N/A
  if (cell?.value == null || cell?.value === 'N/A') {
    return <span>{defaultValue}</span>;
  }
  return formatter(cell.value);
};

// Safe access to viewData property
const getViewData = (cell) => {
  return cell?.row?.original?.viewData || '';
};

// Cell render components with PropTypes
const TotalCell = ({ cell }) => {
  const viewData = getViewData(cell);

  // Handle opportunities
  if (viewData === 'Opportunities Generated' || viewData === 'Opportunities Closed') {
    if (cell?.value === 'N/A') return <span>N/A</span>;
    if (cell?.value === null || cell?.value === 0) return <span>0</span>;
    return <span>{formatNumberUsingUnit(Math.trunc(cell?.value || 0))}</span>;
  }

  // Handle percentage values
  if (percentValueSymbolArray.includes(viewData)) {
    return safeRenderValue(cell, (value) => <span>{formatNumberUsingUnit(value, '%')}</span>);
  }

  // Default - currency format
  return safeRenderValue(cell, (value) => <span>{formatNumberUsingUnit(value, '$')}</span>);
};

const PlanPercentCell = ({ cell }) =>
  safeRenderValue(cell, (value) => <span>{formatNumberUsingUnit(value, '%')}</span>);

const RegionTotalCell = ({ cell }) => {
  const viewData = getViewData(cell);

  // Safety check for null/undefined cell
  if (cell?.value == null || cell?.value === 'N/A') {
    return <span>N/A</span>;
  }

  // Handle Opportunities Generated To Plan
  if (viewData === 'Opportunities Generated To Plan') {
    if (cell?.value === 'N/A') return <span>N/A</span>;
    if (cell?.value === null || cell?.value === 0) return <span>0</span>;
    return <span>{formatNumberUsingUnit(cell.value, '%')}</span>;
  }

  // Handle percentage values (Close Rate, Cancellation Rate)
  if (percentValueSymbolArray.includes(viewData)) {
    // Make sure we add % even if formatNumberUsingUnit doesn't
    const valueStr = cell.value === 'N/A' ? 'N/A' : `${cell.value}%`;
    return <span>{valueStr}</span>;
  }

  // Default - currency format
  return <span>{formatNumberUsingUnit(cell.value, '$')}</span>;
};

const AveragePercentCell = ({ cell }) => {
  const viewData = getViewData(cell);

  // Safety check for null/undefined cell
  if (cell?.value == null || cell?.value === 'N/A') {
    return <span>N/A</span>;
  }

  // Handle percentage values (includes Close Rate, Cancellation Rate, and Opportunities Generated To Plan)
  if ([...percentValueSymbolArray, 'Opportunities Generated To Plan'].includes(viewData)) {
    return <span>{formatNumberUsingUnit(cell.value, '%')}</span>;
  }

  // Default - currency format
  return <span>{formatNumberUsingUnit(cell.value, '$')}</span>;
};

// Define PropTypes for all cell components
const cellPropType = PropTypes.shape({
  value: PropTypes.any,
  row: PropTypes.shape({
    original: PropTypes.shape({
      viewData: PropTypes.string,
      columnName: PropTypes.string,
    }),
  }),
});

TotalCell.propTypes = {
  cell: cellPropType.isRequired,
};

PlanPercentCell.propTypes = {
  cell: cellPropType.isRequired,
};

RegionTotalCell.propTypes = {
  cell: cellPropType.isRequired,
};

AveragePercentCell.propTypes = {
  cell: cellPropType.isRequired,
};

// Export column configurations with the validated cell components
export const columnsArray = [
  {
    Header: ' ',
    accessor: 'columnName',
    enableRowSpan: true,
    style: { width: '294px' },
  },
  {
    Header: `Year`,
    accessor: 'viewData',
    style: { width: '274px' },
  },
  {
    Header: 'Total',
    accessor: 'total',
    Cell: TotalCell,
    style: { width: '164px' },
  },
  {
    Header: '% to Plan',
    accessor: 'percentToPlan',
    Cell: PlanPercentCell,
    style: { width: '108px' },
  },
];

export const columnsArrayDR = [
  {
    Header: ' ',
    accessor: 'columnName',
    enableRowSpan: true,
  },
  {
    Header: `Year`,
    accessor: 'viewData',
  },
  {
    Header: 'Total',
    accessor: 'total',
  },
  {
    Header: '% to Plan',
    accessor: 'percentToPlan',
  },
  {
    Header: '% to prev year',
    accessor: 'percentToPrevYear',
  },
];

export const columnsArrayYTD = [
  {
    Header: ' ',
    accessor: 'columnName',
    enableRowSpan: true,
    style: { width: '294px' },
  },
  {
    Header: `YTD view ${new Date().getFullYear() + yearPlusValue}`,
    accessor: 'viewData',
    style: { width: '274px' },
  },
  {
    Header: 'Total',
    accessor: 'total',
    Cell: TotalCell,
    style: { width: '164px' },
  },
  {
    Header: '% to Plan',
    accessor: 'percentToPlan',
    Cell: PlanPercentCell,
    style: { width: '108px' },
  },
];

export const columnsArrayYTDdR = [
  {
    Header: ' ',
    accessor: 'columnName',
    enableRowSpan: true,
  },
  {
    Header: `YTD view ${new Date().getFullYear() + 1}`,
    accessor: 'viewData',
  },
  {
    Header: 'Total',
    accessor: 'total',
  },
  {
    Header: '% to Plan',
    accessor: 'percentToPlan',
  },
  {
    Header: '% to prev year',
    accessor: 'percentToPrevYear',
  },
];

export const columnsRegion = [
  {
    Header: ' ',
    accessor: 'columnName',
    enableRowSpan: true,
    style: { width: '294px' },
  },
  {
    Header: 'Comparison YTD View',
    accessor: 'viewData',
    style: { width: '274px' },
  },
  {
    Header: 'Total',
    accessor: 'total',
    Cell: RegionTotalCell,
    style: { width: '164px' },
  },
  {
    Header: 'Regional Average',
    accessor: 'percentToRegion',
    Cell: AveragePercentCell,
  },
  {
    Header: 'GALE Average',
    accessor: 'percentToGaleAvg',
    Cell: AveragePercentCell,
  },
];

export const columnsRegionDR = [
  {
    Header: ' ',
    accessor: 'columnName',
    enableRowSpan: true,
    style: { width: '294px' },
  },
  {
    Header: 'Comparison YTD View',
    accessor: 'viewData',
    style: { width: '274px' },
  },
  {
    Header: 'Total',
    accessor: 'total',
    style: { width: '164px' },
  },
  {
    Header: '% vs Region',
    accessor: 'percentToRegion',
  },
  {
    Header: '% vs GALE avg',
    accessor: 'percentToGaleAvg',
  },
];

export const pivot_config = {
  measuredResult: [
    {
      label: 'Spend',
      value: 'spend',
    },
    {
      label: 'Opportunities Generated',
      value: 'opps_generated',
    },
    {
      label: 'Opportunities Closed',
      value: 'opps_closed',
    },
    {
      label: 'Sales',
      value: 'sales',
    },
  ],
  calculatedPerformanceIndicators: [
    {
      label: 'Cost per Opportunity',
      value: 'cpo',
    },
    {
      label: 'Return on Marketing Investment',
      value: 'romi',
    },
    {
      label: 'Average Job Size',
      value: 'avg_job_size',
    },
    {
      label: 'Close Rate',
      value: 'close_rate',
    },
    {
      label: 'Cancellation Rate',
      value: 'cancellation_rate',
    },
  ],
};

export const pivot_config_region_comp = {
  measuredResult: [
    {
      label: 'Spend',
      value: 'spend',
    },
    {
      label: 'Opportunities Generated To Plan',
      value: 'opps_to_plan',
    },
    {
      label: 'Sales',
      value: 'sales',
    },
  ],
  calculatedPerformanceIndicators: [
    {
      label: 'Cost per Opportunity',
      value: 'cpo',
    },
    {
      label: 'Return on Marketing Investment',
      value: 'romi',
    },
    {
      label: 'Average Job Size',
      value: 'avg_job_size',
    },
    {
      label: 'Close Rate',
      value: 'close_rate',
    },
    {
      label: 'Cancellation Rate',
      value: 'cancel_rate',
    },
  ],
};

export const currentPeriodFiltersToUpdate = [
  {
    col: 'fiscal_year',
    op: '==',
    val: 2023,
  },
  {
    col: 'fiscal_week',
    op: '<=',
    val: 3,
  },
  {
    col: 'fiscal_week',
    op: '>=',
    val: 1,
  },
  {
    col: 'store_id',
    op: 'IN',
    val: 700,
  },
];

export const comparePeriodFiltersToUpdate = [
  {
    col: 'fiscal_year',
    op: '==',
    val: 2022,
  },
  {
    col: 'fiscal_week',
    op: '<=',
    val: 10,
  },
  {
    col: 'fiscal_week',
    op: '>=',
    val: 3,
  },
  {
    col: 'store_id',
    op: '==',
    val: 700,
  },
];

export const currentYearFiltersToUpdate = [
  {
    col: 'fiscal_year',
    op: '==',
    val: 2023,
  },
  {
    col: 'fiscal_week',
    op: '==',
    val: 10,
  },
  {
    col: 'store_id',
    op: '==',
    val: 700,
  },
];

export const comparisonYearFiltersToUpdate = [
  {
    col: 'fiscal_year',
    op: '==',
    val: 2020,
  },
  {
    col: 'fiscal_week',
    op: '==',
    val: 10,
  },
  {
    col: 'store_id',
    op: '==',
    val: 700,
  },
];

export const regionComparisonFiltersToUpdate = [
  {
    col: 'fiscal_year',
    op: '==',
    val: 2023,
  },
  {
    col: 'store_id',
    op: 'in',
    val: 700,
  },
];
